// lib/telefonie/nummer.ts
// EINE Wahrheit für Telefonnummern. Die Datenbank hält sie frei formatiert an fünf
// Stellen (kontakt_kanaele, mieter, eigentuemer, kontakte, dienstleister); hier
// werden sie auf E.164 gebracht. Bewusst streng: lieber kein Ergebnis als ein
// falscher Treffer bei der späteren Anrufer-Erkennung.

/** Nur Ziffern und ein führendes Plus; alle üblichen Trennzeichen fallen weg. */
const TRENNZEICHEN = /[\s()\-/.]/g;
/** „(0)" — Amtsnull in Klammern, z. B. „+49 (0)6421". Muss vor dem Trennzeichen-
 *  Stripping weg, sonst bleibt eine nackte, falsche 0 in der Nummer stehen. */
const KLAMMER_NULL = /\(\s*0\s*\)/g;
/** Amtsnull direkt hinter der deutschen Landesvorwahl, z. B. „+49 0 6421" oder
 *  „+49 (0)6421" (nach KLAMMER_NULL bereits ohne Klammern). Bewusst nur für +49 —
 *  eine generische „Null nach Landesvorwahl weg" wäre z. B. für Italien falsch. */
const DE_AMTSNULL_NACH_LANDESVORWAHL = /^\+490(?=\d)/;

/** Gemeinsame Bereinigung für Normalisierung und Rückfall: Amtsnull in Klammern
 *  und alle üblichen Trennzeichen raus. */
function bereinige(roh: string): string {
  return roh.trim().replace(KLAMMER_NULL, '').replace(TRENNZEICHEN, '');
}

/**
 * Roh-Nummer → E.164 (`+49…`) oder null.
 * Regeln: Trennzeichen weg, `00` → `+`, führende `0` → `+49`, deutsche Amtsnull
 * nach der Landesvorwahl weg. Alles, was danach nicht aus `+` und 8–15 Ziffern
 * besteht, gilt als unbrauchbar.
 */
export function normalisiereNummer(roh: string | null | undefined): string | null {
  if (typeof roh !== 'string') return null;
  const kompakt = bereinige(roh);
  if (kompakt === '') return null;

  let e164: string;
  if (kompakt.startsWith('+')) e164 = kompakt;
  else if (kompakt.startsWith('00')) e164 = `+${kompakt.slice(2)}`;
  else if (kompakt.startsWith('0')) e164 = `+49${kompakt.slice(1)}`;
  else return null; // ohne Vorwahl-Kennung nicht eindeutig zuzuordnen

  e164 = e164.replace(DE_AMTSNULL_NACH_LANDESVORWAHL, '+49');

  return /^\+[1-9]\d{7,14}$/.test(e164) ? e164 : null;
}

/**
 * `tel:`-Ziel für einen Wähl-Link. Bevorzugt die normalisierte Nummer; sonst die
 * bereinigte Rohnummer, damit Kurzwahlen und Sonderrufnummern wählbar bleiben.
 * Der Rückfall lässt kein `+` zu: eine `+`-Nummer, die an der E.164-Prüfung
 * scheitert, ist Datenmüll und kein Kurzwahlkandidat. Nach oben gedeckelt auf
 * 15 Ziffern (E.164-Maximum), damit keine unplausibel lange Ziffernfolge wählbar wird.
 */
export function telHref(roh: string | null | undefined): string | null {
  const e164 = normalisiereNummer(roh);
  if (e164) return `tel:${e164}`;
  if (typeof roh !== 'string') return null;
  const kompakt = bereinige(roh);
  return /^\d{3,15}$/.test(kompakt) ? `tel:${kompakt}` : null;
}
